import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import mcpBotIcon from '../../../static/images/logo/rebrand/icons/mcp-bot.webp';

export const Widget = ({
  type,
  gsap,
  ScrollTrigger,
  chatFlow,
  integrations: initialIntegrations,
  tools: initialTools,
}) => {
  // Create state copies to track which integrations and tools are active
  const [integrations, setIntegrations] = useState(
    initialIntegrations?.map(integration => ({ ...integration })) || []
  );
  const [tools, setTools] = useState(
    initialTools?.map(tool => ({ ...tool })) || []
  );
  // Use state to track if component is mounted
  const [isMounted, setIsMounted] = useState(false);

  // First effect - mark component as mounted
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Second effect - refresh ScrollTrigger after component is confirmed mounted
  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') return;

    // Use a more substantial delay to ensure DOM is fully ready
    const refreshTimeout = setTimeout(() => {
      if (ScrollTrigger) {
        // Force recalculation of all ScrollTrigger instances
        ScrollTrigger.refresh(true);
      }
    }, 300); // Longer delay for more reliability

    return () => clearTimeout(refreshTimeout);
  }, [isMounted, ScrollTrigger]);

  const chatRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  useEffect(() => {
    // Don't try to initialize if component isn't mounted or we don't have refs/ScrollTrigger yet
    if (!isMounted || !chatRef.current || !ScrollTrigger) return;

    // Use a longer delay to ensure DOM is fully ready for ScrollTrigger
    const initTimer = setTimeout(() => {
      try {
        // Kill any existing instance with this ID first
        const existingInstance = ScrollTrigger.getById('mcp-widget-animation');
        if (existingInstance) {
          existingInstance.kill();
        }

        // Create new instance
        ScrollTrigger.create({
          trigger: chatRef.current,
          start: 'top 80%', // Trigger when top of chat hits 80% of viewport
          onEnter: () => {
            if (!hasStarted) {
              setHasStarted(true);
              renderChat(0);
            }
          },
          // markers: process.env.NODE_ENV === 'development', // Show markers in development
          id: 'mcp-widget-animation', // ID for easier debugging
        });
      } catch (error) {
        console.error('Error creating ScrollTrigger:', error);
      }
    }, 500); // Longer delay for more reliability

    return () => {
      clearTimeout(initTimer);
      const instance = ScrollTrigger.getById('mcp-widget-animation');
      if (instance) instance.kill();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, hasStarted, chatRef.current, ScrollTrigger]);

  const scrollToBottom = () => {
    if (chatRef?.current) {
      // Use requestAnimationFrame to ensure DOM has updated before scrolling
      requestAnimationFrame(() => {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      });
    }
  };

  const renderChat = index => {
    if (index >= chatFlow.length) {
      // Animation is complete when we've gone through all messages
      setIsAnimationComplete(true);
      return;
    }

    const message = chatFlow[index];

    if (message.from === 'user') {
      setMessages(prev => [
        ...prev,
        { type: 'text', from: 'user', content: message.text, isNew: true },
      ]);
      setTimeout(() => renderChat(index + 1), 1000);
    } else if (message.from === 'ai') {
      showTypingIndicator(() => {
        renderAIParts(message.parts, 0, () => renderChat(index + 1));
      });
    }
  };

  // Function to check for mentions and activate toggles
  const checkForMentions = text => {
    if (!text) return;

    // Convert text to lowercase for case-insensitive matching
    const lowerText = text.toLowerCase();

    // Check integrations
    setIntegrations(currentIntegrations => {
      let hasChanges = false;

      // Create a new array only if we need to make changes
      const updated = currentIntegrations.map(integration => {
        // If this integration is mentioned and not already active
        if (
          lowerText.includes(integration.name.toLowerCase()) &&
          !integration.status
        ) {
          hasChanges = true;
          return { ...integration, status: true };
        }
        // Otherwise keep it as is (preserving any previously activated status)
        return integration;
      });

      // Only return the new array if we made changes
      return hasChanges ? updated : currentIntegrations;
    });

    // Check tools using the same pattern to preserve state
    setTools(currentTools => {
      let hasChanges = false;

      const updated = currentTools.map(tool => {
        // Get the base name (e.g., "Shopify" from "Shopify_list_users")
        const baseName = tool.name.split('_')[0];

        // If this tool is mentioned and not already active
        if (
          (lowerText.includes(tool.name.toLowerCase()) ||
            lowerText.includes(baseName.toLowerCase())) &&
          !tool.status
        ) {
          hasChanges = true;
          return { ...tool, status: true };
        }
        // Otherwise keep it as is
        return tool;
      });

      return hasChanges ? updated : currentTools;
    });
  };

  const showTypingIndicator = callback => {
    setMessages(prev => [...prev, { type: 'typing', from: 'ai', isNew: true }]);
    setTimeout(() => {
      setMessages(prev => prev.filter(msg => msg.type !== 'typing'));
      callback();
    }, 1000);
  };

  const renderAIParts = (parts, partIndex, doneCallback) => {
    if (partIndex >= parts.length) {
      return doneCallback();
    }

    const part = parts[partIndex];
    let msg;

    if (typeof part === 'string') {
      // For text parts, we'll start with an empty content and add characters one by one
      msg = {
        type: 'text',
        from: 'ai',
        content: '',
        fullContent: part,
        isNew: true,
        isTyping: true,
        isFirstResponse: partIndex === 0,
        charIndex: 0,
      };
    } else if (part.type === 'code') {
      // For code parts, we display as before (no typing animation)
      msg = {
        type: 'code',
        from: 'ai',
        content: part.content,
        title: part.title,
        isNew: true,
      };
    } else if (part.type === 'list') {
      // For lists, we'll also animate typing
      const fullContent = part.items.join('\n');

      msg = {
        type: 'text',
        from: 'ai',
        content: '',
        fullContent: fullContent,
        isNew: true,
        isTyping: true,
        charIndex: 0,
      };
    } else if (part.type === 'option') {
      // For option parts, we display with no typing animation
      msg = {
        type: 'option',
        from: 'ai',
        options: part.options,
        isNew: true,
      };
    } else if (part.type === 'button') {
      // For button parts, we display with no typing animation
      msg = {
        type: 'button',
        from: 'ai',
        label: part.label,
        isNew: true,
      };
    }

    // Add the initial message to the messages array and get its index
    let messageIndex;
    setMessages(prev => {
      messageIndex = prev.length;
      return [...prev, msg];
    });

    // For non-typing messages, check for mentions and continue to next part after a delay
    if (!msg.isTyping) {
      // Check for mentions in code blocks immediately
      if (
        msg.type === 'code' ||
        msg.type === 'option' ||
        msg.type === 'button'
      ) {
        if (msg.title) checkForMentions(msg.title);
        if (msg.content) checkForMentions(msg.content);
      }

      setTimeout(() => renderAIParts(parts, partIndex + 1, doneCallback), 900);
      return;
    }

    // For typing messages, we'll handle the animation separately
    const typeNextContent = () => {
      setMessages(prev => {
        const updatedMessages = [...prev];
        const currentMsg = { ...updatedMessages[messageIndex] };

        if (currentMsg.charIndex < currentMsg.fullContent.length) {
          // Determine if we should type a single character or a whole word
          const remainingContent = currentMsg.fullContent.substring(
            currentMsg.charIndex
          );

          // 70% chance to type a character, 30% chance to type a word
          const typeCharacter = Math.random() < 0.7;

          if (typeCharacter) {
            // Type a single character
            const newChar = currentMsg.fullContent[currentMsg.charIndex];
            currentMsg.content += newChar;
            currentMsg.charIndex++;

            // No mention checking during typing
          } else {
            // Type a whole word or up to space/punctuation
            const nextSpaceMatch = remainingContent.match(
              /^([^\s.,!?;:]+)(\s|[.,!?;:]|$)/
            );
            if (nextSpaceMatch) {
              const wordToAdd = nextSpaceMatch[0];
              currentMsg.content += wordToAdd;
              currentMsg.charIndex += wordToAdd.length;

              // No mention checking during typing
            } else {
              // Fallback to single character if no match
              currentMsg.content +=
                currentMsg.fullContent[currentMsg.charIndex];
              currentMsg.charIndex++;
            }
          }

          // Keep typing flag active
          currentMsg.isTyping = true;
          updatedMessages[messageIndex] = currentMsg;

          // Scroll to bottom with each character/word update
          requestAnimationFrame(() => {
            if (chatRef?.current) {
              chatRef.current.scrollTop = chatRef.current.scrollHeight;
            }
          });

          // Random delay between 20ms and 80ms for natural timing
          const typingSpeed = Math.floor(Math.random() * 60) + 2;
          setTimeout(typeNextContent, typingSpeed);
        } else {
          // Typing complete
          currentMsg.isTyping = false;
          updatedMessages[messageIndex] = currentMsg;

          // Check for mentions after this part is complete
          if (currentMsg.fullContent) {
            checkForMentions(currentMsg.fullContent);
          }

          // Move to next part
          setTimeout(
            () => renderAIParts(parts, partIndex + 1, doneCallback),
            500
          );
        }

        return updatedMessages;
      });
    };

    // Start typing animation
    setTimeout(typeNextContent, 100);
  };

  // Configuration object for cursor styling and animation
  const cursorConfig = {
    width: '2px',
    height: '14px', // Slightly taller than before
    color: 'currentColor', // Uses text color
    blinkSpeed: 0.5, // Animation duration in seconds
    margin: '-4px 0 0 0.1em', // Adjust position
  };

  const ChatBubble = ({ message, onMount }) => {
    const elRef = useRef();
    const animatedRef = useRef(false);
    const cursorRef = useRef(null);
    const dots = [0, 200, 400];

    useEffect(() => {
      if (!elRef.current || !message.isNew || animatedRef.current) return;

      gsap.fromTo(
        elRef.current,
        {
          opacity: 0,
          y: 10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          onComplete: () => {
            animatedRef.current = true;
            if (onMount) onMount();
          },
        }
      );

      // Mark the message as no longer new after animation
      message.isNew = false;
    }, [message, onMount]);

    // Create blinking cursor effect with enhanced debugging
    useEffect(() => {
      if (!cursorRef.current || !message.isTyping) return;

      const blink = gsap.to(cursorRef.current, {
        opacity: 0,
        duration: cursorConfig.blinkSpeed,
        repeat: -1,
        yoyo: true,
      });

      return () => {
        blink.kill();
      };
    }, [message.isTyping]);

    return (
      <div
        ref={elRef}
        className={classNames('flex flex-row', {
          'justify-end': message.from === 'user',
          'justify-start gap-4': message.from === 'ai',
        })}
      >
        {message.from === 'ai' && (
          <div
            className={classNames(
              '-mt-0.5 h-6 w-6 overflow-hidden rounded-full',
              {
                invisible:
                  !message.isFirstResponse && message.type !== 'typing',
              }
            )}
          >
            <img
              src={mcpBotIcon}
              alt="mcp-bot"
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <div
          className={classNames(
            'max-w-[80%] whitespace-pre-wrap text-sm text-white dark:text-black sm:max-w-[55%]',
            {
              'self-end rounded-lg border border-white/10 bg-azure-900 px-3 py-2 dark:border-black/10 dark:bg-white':
                message.from === 'user',
              'rounded-lg border border-white/10 bg-black/5 p-2 dark:border-black/10':
                message.type === 'code',
              '-mt-1': message.type === 'button' || message.type === 'option',
            }
          )}
        >
          {message.type === 'code' ? (
            <CodeBlock title={message.title} content={message.content} />
          ) : message.type === 'option' ? (
            <div className="flex w-fit items-center gap-2">
              {message.options &&
                message.options.map(option => (
                  <button
                    key={option}
                    className="flex items-center justify-center rounded-lg border border-white/10 bg-white/3 px-8 py-2 text-sm first:bg-black/5 dark:border-black/10"
                  >
                    {option}
                  </button>
                ))}
            </div>
          ) : message.type === 'button' ? (
            <div>
              <button className="flex items-center justify-center rounded-lg border border-white/10 bg-white/3 px-8 py-2 text-sm dark:border-black/10">
                {message.label}
              </button>
            </div>
          ) : message.type === 'typing' ? (
            <div className="flex w-fit items-center gap-1 py-2">
              {dots.map((delay, idx) => (
                <div
                  key={idx}
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-white dark:bg-black/60"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
          ) : (
            <>
              {message.content}
              {message.isTyping && (
                <span
                  ref={cursorRef}
                  className="typing-cursor inline-block"
                  style={{
                    width: cursorConfig.width,
                    height: cursorConfig.height,
                    backgroundColor: cursorConfig.color,
                    margin: cursorConfig.margin,
                    verticalAlign: 'middle',
                    borderRadius: '1px',
                  }}
                  data-testid="typing-cursor"
                  title="Typing cursor"
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const CodeBlock = ({ title, content }) => {
    return (
      <div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.7071 3.79289C14.0976 4.18342 14.0976 4.81658 13.7071 5.20711L6.70713 12.2071C6.51016 12.4041 6.23998 12.51 5.96164 12.4993C5.6833 12.4886 5.42204 12.3623 5.24076 12.1508L2.24076 8.65079C1.88134 8.23147 1.9299 7.60017 2.34923 7.24074C2.76855 6.88132 3.39985 6.92988 3.75927 7.34921L6.0565 10.0293L12.2929 3.79289C12.6834 3.40237 13.3166 3.40237 13.7071 3.79289Z"
                fill="#7CC0B8"
              />
            </svg>
            <span className="mr-2 text-[13px]">{title}</span>
          </div>
        </div>
        {content && (
          <div className="mt-2 rounded-lg border border-white/10 bg-white/3 px-4 py-2 font-mono dark:border-black/10 dark:bg-black/3">
            {content}
          </div>
        )}
      </div>
    );
  };

  const Toggle = ({ status }) => {
    // Initialize local state with the prop value
    const [cStatus, setCStatus] = useState(status);

    // Sync local state with prop changes for smooth transitions
    useEffect(() => {
      setCStatus(status);
    }, [status]);

    // Handle direct toggle clicks
    const toggleFunc = () => {
      setCStatus(bool => !bool);
    };

    return (
      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
      <div
        className={classNames(
          'relative h-[18px] w-8 rounded-[5px] border border-white/20 p-px dark:border-black/20',
          {
            'bg-jade-400': cStatus,
            'bg-black/20': !cStatus,
          }
        )}
        onClick={toggleFunc}
        role="button"
        tabIndex="0"
        aria-label="Toggle button"
      >
        <div
          className={classNames(
            'absolute left-px top-px h-3.5 w-3.5 transform-gpu rounded-[3px] bg-white transition-all duration-300 ease-in-out will-change-transform',
            {
              'translate-x-full': cStatus,
              'translate-x-0': !cStatus,
            }
          )}
        />
      </div>
    );
  };

  // Add a CSS class for the shadow to avoid repaints during scroll
  return (
    <div
      className={classNames(
        // 'transform-gpu shadow-widget will-change-transform',
        'overflow-hidden rounded-lg border border-white/20',
        {
          'dark !border-black/20': type === 'dark',
        }
      )}
    >
      <div
        className="grid h-full w-full grid-cols-1 text-white backdrop-blur dark:text-black sm:grid-cols-[55px_1fr_260px]"
        style={{
          background:
            type === 'dark'
              ? 'linear-gradient(127deg, rgba(255, 255, 255, 0.10) -140.69%, #FFF 65.52%)'
              : 'linear-gradient(127deg, #EAEAEA -140.69%, #0B21A6 65.52%)',
        }}
      >
        <div className="hidden border-r border-r-white/20 dark:border-r-black/20 sm:block">
          <div className="flex h-12 items-center justify-center border-b border-b-white/20 text-base font-semibold tracking-[0.16px] dark:border-b-black/20">
            Ag
          </div>
          <div className="flex h-12 items-center justify-center border-b border-b-white/20 bg-white/5 text-base font-semibold tracking-[0.16px] dark:border-b-black/20 dark:bg-black/5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                className="fill-white dark:fill-black"
                d="M13 0C14.6569 0 16 1.34315 16 3V11C16 12.6569 14.6569 14 13 14H7.47168C7.31675 14.0001 7.164 14.0362 7.02539 14.1055L4.3418 15.4473C2.34709 16.4446 0 14.9938 0 12.7637V3C0 1.34315 1.34315 0 3 0H13ZM3 2C2.44772 2 2 2.44772 2 3V12.7637C2 13.5071 2.78237 13.9907 3.44727 13.6582L6.13086 12.3164C6.54718 12.1083 7.00626 12.0001 7.47168 12H13C13.5523 12 14 11.5523 14 11V3C14 2.44772 13.5523 2 13 2H3ZM9 8C9.55228 8 10 8.44772 10 9C10 9.55228 9.55228 10 9 10H5C4.44772 10 4 9.55228 4 9C4 8.44772 4.44772 8 5 8H9ZM11 4C11.5523 4 12 4.44772 12 5C12 5.55228 11.5523 6 11 6H5C4.44772 6 4 5.55228 4 5C4 4.44772 4.44772 4 5 4H11Z"
                fillOpacity="0.3"
              />
            </svg>
          </div>
        </div>
        <div className="widget relative">
          <div className="hidden h-12 items-center justify-start border-b border-b-white/20 px-4 text-base font-semibold tracking-[0.16px] dark:border-b-black/20 sm:flex">
            New Chat
          </div>
          <div
            ref={chatRef}
            className={classNames('h-[640px] space-y-4 px-6 pb-28 pt-6', {
              'overflow-y-scroll': isAnimationComplete,
              'overflow-hidden': !isAnimationComplete,
            })}
          >
            {messages.map((msg, idx) => (
              <ChatBubble key={idx} message={msg} onMount={scrollToBottom} />
            ))}
          </div>
          <div className="absolute bottom-6 left-6 right-6 flex h-[60px] items-center justify-between rounded-lg border border-white/20 bg-white/10 px-6 py-4 text-sm backdrop-blur-[18px] dark:border-black/20 dark:bg-white">
            <span>Write a message</span>
            <span className="flex h-8 w-14 cursor-pointer items-center justify-center rounded-full bg-white/30 dark:bg-black/5">
              Ask
            </span>
          </div>
        </div>
        <div className="hidden border-l border-l-white/20 dark:border-l-black/20 sm:block">
          <div className="flex h-12 items-center justify-start border-b border-b-white/20 px-4 text-base font-semibold tracking-[0.16px] dark:border-b-black/20">
            MCP Settings
          </div>
          <div>
            <div className="p-4 text-sm font-medium">Claude Sonnet 3.7</div>
            {integrations.length > 0 && (
              <>
                <div className="border-b border-b-white/20 p-4 text-sm font-medium dark:border-b-black/20">
                  Integrations
                </div>
                <div className="min-h-28 space-y-3 p-4">
                  {integrations.map(integration => (
                    <div
                      key={integration.name}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={integration.icon}
                          alt={integration.name}
                          className="h-5 w-5"
                        />
                        <div className="text-sm font-medium">
                          {integration.name}
                        </div>
                      </div>
                      <Toggle status={integration.status} />
                    </div>
                  ))}
                </div>
              </>
            )}
            {tools.length > 0 && (
              <>
                <div className="border-b border-b-white/20 p-4 text-sm font-medium dark:border-b-black/20">
                  Tools
                </div>
                <div className="space-y-3 p-4">
                  {tools.map(tool => (
                    <div
                      key={tool.name}
                      className="flex items-center justify-between"
                    >
                      <div className="font-mono text-xs font-medium">
                        {tool.name}
                      </div>
                      <Toggle status={tool.status} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
